import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, RefreshCw, X } from 'lucide-react';
import api from '../api';

function SyncPill() {
  const [syncStatus, setSyncStatus] = useState('idle');
  const [showDetails, setShowDetails] = useState(false);
  const [connections, setConnections] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    loadSyncStatus();
    const interval = setInterval(loadSyncStatus, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const loadSyncStatus = async () => {
    try {
      const [connectionsData, logsData] = await Promise.all([
        api.getSyncConnections(),
        api.getSyncLogs(),
      ]);
      setConnections(connectionsData);
      setLogs(logsData);

      // Determine overall status
      if (connectionsData.some((c) => c.sync_status === 'syncing')) {
        setSyncStatus('syncing');
      } else if (connectionsData.some((c) => c.sync_status === 'error')) {
        setSyncStatus('error');
      } else if (connectionsData.some((c) => c.sync_status === 'success')) {
        setSyncStatus('success');
      } else {
        setSyncStatus('idle');
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <RefreshCw className="h-4 w-4 animate-spin sync-status-syncing" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 sync-status-success" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 sync-status-error" />;
      default:
        return <RefreshCw className="h-4 w-4" />;
    }
  };

  const getStatusText = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'Syncing...';
      case 'success':
        return 'Synced';
      case 'error':
        return 'Sync Error';
      default:
        return 'Sync Ready';
    }
  };

  return (
    <>
      <div
        className="sync-pill"
        onClick={() => setShowDetails(true)}
        data-testid="sync-pill"
      >
        {getStatusIcon()}
        <span>{getStatusText()}</span>
      </div>

      {showDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="sync-details-modal">
          <div className="bg-card p-6 rounded-lg max-w-2xl w-full mx-4 border border-border max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Sync Status</h2>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 hover:bg-secondary rounded-md transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-2">Connections</h3>
                {connections.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sync connections configured</p>
                ) : (
                  <div className="space-y-2">
                    {connections.map((conn) => (
                      <div
                        key={conn._id}
                        className="p-3 bg-muted rounded-md flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium">{conn.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {conn.system} • {conn.sync_status}
                          </p>
                        </div>
                        {conn.last_sync && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(conn.last_sync).toLocaleString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Recent Sync Logs</h3>
                {logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sync logs yet</p>
                ) : (
                  <div className="space-y-2">
                    {logs.slice(0, 5).map((log) => (
                      <div
                        key={log._id}
                        className="p-3 bg-muted rounded-md"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium capitalize ${log.status === 'failed' ? 'text-destructive' : ''}`}>
                            {log.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.started_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {log.items_synced} synced, {log.items_failed} failed
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SyncPill;
